<template>
    <div class="flex w-0 flex-1 flex-col overflow-hidden">
        <ToggleSidebar />

        <main
            class="focus:outline-none relative z-0 flex-1 overflow-y-auto"
            tabindex="0"
        >
            <div class="pb-6 pt-2 md:py-6">
                <div class="mx-auto px-4 sm:px-6 lg:px-8">
                    <div class="md:flex md:items-center md:justify-between">
                        <div class="min-w-0 flex-1 flex items-center">
                            <BasePageTitle :title="pageTitle" />
                        </div>
                        <BaseSpinner :isLoading="isLoading" />
                    </div>
                </div>

                <div class="mx-auto px-0 py-4 sm:px-6 md:px-8">
                    <div class="rounded bg-white shadow-md sm:rounded-lg p-6">
                        <div class="mb-6">
                            <div class="flex items-center justify-between mb-4">
                                <div class="flex items-center space-x-2">
                                    <div
                                        v-for="(step, index) in steps"
                                        :key="index"
                                        class="flex items-center"
                                    >
                                        <div
                                            class="flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium transition-colors"
                                            :class="getStepClass(index)"
                                        >
                                            {{ index + 1 }}
                                        </div>
                                        <div
                                            v-if="index < steps.length - 1"
                                            class="w-12 h-0.5 mx-2"
                                            :class="currentStep > index ? 'bg-blue-600' : 'bg-gray-300'"
                                        ></div>
                                    </div>
                                </div>
                            </div>
                            <div class="text-sm text-gray-600">
                                Step {{ currentStep + 1 }} of {{ steps.length }}: {{ steps[currentStep].title }}
                            </div>
                        </div>

                        <div v-if="currentStep === 0" class="space-y-6">
                            <div>
                                <label class="block text-sm font-medium text-gray-700 mb-2">
                                    Story Title *
                                </label>
                                <input
                                    v-model="story.title"
                                    type="text"
                                    placeholder="Enter a title for your story"
                                    class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    :class="{ 'border-red-500': errors.title }"
                                />
                                <p v-if="errors.title" class="mt-1 text-sm text-red-600">{{ errors.title }}</p>
                            </div>

                            <div>
                                <label class="block text-sm font-medium text-gray-700 mb-2">
                                    Cover Video *
                                </label>
                                <p class="text-sm text-gray-500 mb-3">
                                    Upload a video that will be the cover of your story
                                </p>
                                
                                <div v-if="coverPreviewUrl && !isReplacingCoverVideo" class="space-y-3">
                                    <div class="relative w-full max-w-md aspect-[9/16] bg-gray-100 rounded-lg overflow-hidden mx-auto">
                                        <hls-video-player
                                            v-if="story.muxAsset && story.muxAsset.playbackId"
                                            :playback-id="story.muxAsset.playbackId"
                                            :play-on-hover="false"
                                            :muted="true"
                                            :loop="false"
                                            :playsinline="true"
                                            :autoplay="false"
                                            :show-play-overlay="true"
                                            alt="Story cover video"
                                            class="absolute inset-0"
                                        />
                                        <video
                                            v-else-if="story.muxUrl"
                                            :src="story.muxUrl"
                                            controls
                                            class="w-full h-full object-contain"
                                        ></video>
                                    </div>
                                    <button
                                        @click="isReplacingCoverVideo = true"
                                        type="button"
                                        class="w-full flex items-center justify-center px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                    >
                                        <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                                        </svg>
                                        Replace Video
                                    </button>
                                </div>
                                
                                <div v-else>
                                    <UploadcareInline
                                        ref="coverVideoUploader"
                                        ctx-name="story-cover-video"
                                        input-accept-types="video/*"
                                        :tabs="'local, url'"
                                        @success="handleCoverVideoUpload"
                                        @files-removed="handleCoverVideoRemoved"
                                    />
                                    <button
                                        v-if="isReplacingCoverVideo"
                                        @click="cancelReplaceCoverVideo"
                                        type="button"
                                        class="mt-3 w-full flex items-center justify-center px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                    >
                                        Cancel
                                    </button>
                                    <div v-if="story.muxUrl" class="mt-3 p-3 bg-green-50 border border-green-200 rounded-md">
                                        <div class="flex items-center text-sm text-green-800">
                                            <svg class="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                                <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
                                            </svg>
                                            Video uploaded successfully
                                        </div>
                                    </div>
                                </div>
                                
                                <p v-if="errors.muxUrl" class="mt-1 text-sm text-red-600">{{ errors.muxUrl }}</p>
                            </div>

                            <div>
                                <label class="flex items-center">
                                    <input
                                        v-model="story.active"
                                        type="checkbox"
                                        class="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                    />
                                    <span class="ml-2 text-sm text-gray-700">Make story active immediately</span>
                                </label>
                            </div>
                        </div>

                        <div v-if="currentStep === 1" class="space-y-6">
                            <div class="mb-4">
                                <h3 class="text-lg font-medium text-gray-900 mb-2">Story Pages</h3>
                                <p class="text-sm text-gray-500">
                                    Add pages to your story. Each page can have a video, duration, and call-to-action button.
                                </p>
                            </div>

                            <div v-if="pages.length === 0" class="text-center py-8 border-2 border-dashed border-gray-300 rounded-lg">
                                <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
                                </svg>
                                <h3 class="mt-2 text-sm font-medium text-gray-900">No pages yet</h3>
                                <p class="mt-1 text-sm text-gray-500">Get started by adding your first page</p>
                            </div>

                            <div v-else class="space-y-4">
                                <div
                                    v-for="(page, index) in pages"
                                    :key="index"
                                    class="border border-gray-200 rounded-lg p-4 bg-gray-50"
                                >
                                    <div class="flex items-start justify-between mb-3">
                                        <div class="flex items-center">
                                            <span class="inline-flex items-center justify-center w-6 h-6 rounded-full bg-blue-600 text-white text-xs font-medium mr-2">
                                                {{ index + 1 }}
                                            </span>
                                            <span class="text-sm font-medium text-gray-900">Page {{ index + 1 }}</span>
                                        </div>
                                        <div class="flex items-center space-x-2">
                                            <button
                                                v-if="index > 0"
                                                @click="movePageUp(index)"
                                                class="p-1 text-gray-400 hover:text-gray-600"
                                                title="Move up"
                                            >
                                                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 15l7-7 7 7" />
                                                </svg>
                                            </button>
                                            <button
                                                v-if="index < pages.length - 1"
                                                @click="movePageDown(index)"
                                                class="p-1 text-gray-400 hover:text-gray-600"
                                                title="Move down"
                                            >
                                                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                                                </svg>
                                            </button>
                                            <button
                                                @click="editPage(index)"
                                                class="p-1 text-blue-600 hover:text-blue-800"
                                                title="Edit"
                                            >
                                                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                                </svg>
                                            </button>
                                            <button
                                                @click="deletePage(index)"
                                                class="p-1 text-red-600 hover:text-red-800"
                                                title="Delete"
                                            >
                                                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                </svg>
                                            </button>
                                        </div>
                                    </div>
                                    <div class="grid grid-cols-2 gap-4 text-sm">
                                        <div>
                                            <span class="text-gray-500">Duration:</span>
                                            <span class="ml-1 text-gray-900">{{ page.duration }}s</span>
                                        </div>
                                        <div>
                                            <span class="text-gray-500">CTA:</span>
                                            <span class="ml-1 text-gray-900">{{ formatCtaType(page.ctaType) }}</span>
                                        </div>
                                        <div class="col-span-2">
                                            <span class="text-gray-500">Video:</span>
                                            <span class="ml-1 text-gray-900">{{ page.muxUrl ? '✓ Uploaded' : '✗ Not uploaded' }}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <button
                                @click="openPageModal()"
                                class="w-full flex items-center justify-center px-4 py-3 border-2 border-dashed border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:border-gray-400 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                            >
                                <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
                                </svg>
                                Add Page
                            </button>
                        </div>

                        <div v-if="currentStep === 2" class="space-y-6">
                            <div class="mb-4">
                                <h3 class="text-lg font-medium text-gray-900 mb-2">Assign to Venues</h3>
                                <p class="text-sm text-gray-500">
                                    Select which venues should display this story. You can optionally configure advanced settings like scheduling and priority.
                                </p>
                            </div>

                            <div>
                                <label class="block text-sm font-medium text-gray-700 mb-2">
                                    Select Venues
                                </label>
                                <div class="space-y-2 max-h-64 overflow-y-auto border border-gray-300 rounded-md p-3">
                                    <label
                                        v-for="venue in venues"
                                        :key="venue.id"
                                        class="flex items-center p-2 hover:bg-gray-50 rounded cursor-pointer"
                                    >
                                        <input
                                            type="checkbox"
                                            :value="venue.id"
                                            v-model="selectedVenues"
                                            class="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                        />
                                        <span class="ml-2 text-sm text-gray-900">{{ venue.name }}</span>
                                    </label>
                                </div>
                                <p v-if="selectedVenues.length === 0" class="mt-2 text-sm text-gray-500">
                                    No venues selected. The story will not be visible to customers until assigned to at least one venue.
                                </p>
                                <p v-else class="mt-2 text-sm text-gray-600">
                                    {{ selectedVenues.length }} venue{{ selectedVenues.length !== 1 ? 's' : '' }} selected
                                </p>
                            </div>

                            <div v-if="selectedVenues.length > 0">
                                <button
                                    @click="showAdvancedOptions = !showAdvancedOptions"
                                    class="flex items-center text-sm font-medium text-blue-600 hover:text-blue-700"
                                >
                                    <svg
                                        class="w-5 h-5 mr-1 transition-transform"
                                        :class="{ 'rotate-90': showAdvancedOptions }"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                    >
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                                    </svg>
                                    {{ showAdvancedOptions ? 'Hide' : 'Show' }} Advanced Options
                                </button>

                                <div v-if="showAdvancedOptions" class="mt-4 space-y-4 border border-gray-200 rounded-lg p-4 bg-gray-50">
                                    <div
                                        v-for="venueId in selectedVenues"
                                        :key="venueId"
                                        class="border border-gray-300 rounded-md p-4 bg-white"
                                    >
                                        <h4 class="text-sm font-medium text-gray-900 mb-3">
                                            {{ getVenueName(venueId) }}
                                        </h4>
                                        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div>
                                                <label class="block text-sm font-medium text-gray-700 mb-1">
                                                    Start Date (Optional)
                                                </label>
                                                <input
                                                    type="datetime-local"
                                                    v-model="getVenueAssignment(venueId).startDate"
                                                    class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                                                />
                                                <p class="mt-1 text-xs text-gray-500">When to start showing this story</p>
                                            </div>
                                            <div>
                                                <label class="block text-sm font-medium text-gray-700 mb-1">
                                                    End Date (Optional)
                                                </label>
                                                <input
                                                    type="datetime-local"
                                                    v-model="getVenueAssignment(venueId).endDate"
                                                    class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                                                />
                                                <p class="mt-1 text-xs text-gray-500">When to stop showing this story</p>
                                            </div>
                                            <div>
                                                <label class="block text-sm font-medium text-gray-700 mb-1">
                                                    Priority
                                                </label>
                                                <input
                                                    type="number"
                                                    v-model.number="getVenueAssignment(venueId).priority"
                                                    min="0"
                                                    class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                                                />
                                                <p class="mt-1 text-xs text-gray-500">Higher priority stories appear first</p>
                                            </div>
                                            <div>
                                                <label class="flex items-center mt-6">
                                                    <input
                                                        type="checkbox"
                                                        v-model="getVenueAssignment(venueId).active"
                                                        class="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                                    />
                                                    <span class="ml-2 text-sm text-gray-700">Active</span>
                                                </label>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div v-if="currentStep === 3" class="space-y-6">
                            <div class="text-center py-8">
                                <svg class="mx-auto h-12 w-12 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                <h3 class="mt-2 text-lg font-medium text-gray-900">{{ isEditMode ? 'Story Updated Successfully!' : 'Story Created Successfully!' }}</h3>
                                <p class="mt-1 text-sm text-gray-500">
                                    Your story with {{ pages.length }} page{{ pages.length !== 1 ? 's' : '' }} has been {{ isEditMode ? 'updated' : 'created' }} and assigned to {{ selectedVenues.length }} venue{{ selectedVenues.length !== 1 ? 's' : '' }}.
                                </p>
                            </div>

                            <div class="border-t border-gray-200 pt-6">
                                <h4 class="text-sm font-medium text-gray-900 mb-4">What would you like to do next?</h4>
                                <div class="space-y-3">
                                    <button
                                        @click="goToStories"
                                        class="w-full flex items-center justify-center px-4 py-3 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                    >
                                        <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                                        </svg>
                                        View All Stories
                                    </button>
                                    <button
                                        @click="createAnother"
                                        class="w-full flex items-center justify-center px-4 py-3 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                    >
                                        <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
                                        </svg>
                                        Create Another Story
                                    </button>
                                </div>
                            </div>
                        </div>

                        <div class="mt-8 flex justify-between">
                            <button
                                v-if="currentStep > 0 && currentStep < steps.length - 1"
                                @click="previousStep"
                                class="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                            >
                                Back
                            </button>
                            <div v-else></div>

                            <div class="flex space-x-3">
                                <button
                                    v-if="currentStep < steps.length - 1"
                                    @click="cancel"
                                    class="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                >
                                    Cancel
                                </button>
                                <button
                                    v-if="currentStep === 0"
                                    @click="nextStep"
                                    :disabled="isLoading"
                                    class="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {{ isLoading ? (isEditMode ? 'Updating...' : 'Creating...') : (isEditMode ? 'Update Story' : 'Create Story') }}
                                </button>
                                <button
                                    v-if="currentStep === 1"
                                    @click="nextStep"
                                    class="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                >
                                    Continue
                                </button>
                                <button
                                    v-if="currentStep === 2"
                                    @click="finishStory"
                                    :disabled="isLoading"
                                    class="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {{ isLoading ? 'Saving...' : 'Finish' }}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </main>

        <div
            v-if="showPageModal"
            class="fixed inset-0 z-50 overflow-y-auto"
            @click.self="closePageModal"
        >
            <div class="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
                <div class="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"></div>

                <div class="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
                    <div class="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                        <div class="flex items-center justify-between mb-4">
                            <h3 class="text-lg font-medium text-gray-900">
                                {{ editingPageIndex !== null ? 'Edit Page' : 'Add Page' }}
                            </h3>
                            <button
                                @click="closePageModal"
                                class="text-gray-400 hover:text-gray-500"
                            >
                                <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>

                        <div class="space-y-4">
                            <div>
                                <label class="block text-sm font-medium text-gray-700 mb-2">
                                    Video *
                                </label>
                                <UploadcareInline
                                    :ref="`pageVideoUploader-${editingPageIndex}`"
                                    :ctx-name="`page-video-${editingPageIndex || 'new'}`"
                                    input-accept-types="video/*"
                                    :tabs="'local, url'"
                                    @success="handlePageVideoUpload"
                                    @files-removed="handlePageVideoRemoved"
                                />
                                <p v-if="pageErrors.muxUrl" class="mt-1 text-sm text-red-600">{{ pageErrors.muxUrl }}</p>
                                <div v-if="currentPage.muxUrl" class="mt-3 p-3 bg-green-50 border border-green-200 rounded-md">
                                    <div class="flex items-center text-sm text-green-800">
                                        <svg class="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                            <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
                                        </svg>
                                        Video uploaded successfully
                                    </div>
                                </div>
                            </div>

                            <div>
                                <label class="block text-sm font-medium text-gray-700 mb-2">
                                    Duration (seconds) *
                                </label>
                                <input
                                    v-model.number="currentPage.duration"
                                    type="number"
                                    min="1"
                                    max="60"
                                    placeholder="5"
                                    class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    :class="{ 'border-red-500': pageErrors.duration }"
                                />
                                <p v-if="pageErrors.duration" class="mt-1 text-sm text-red-600">{{ pageErrors.duration }}</p>
                                <p class="mt-1 text-sm text-gray-500">How long this page should be displayed (1-60 seconds)</p>
                            </div>

                            <div>
                                <label class="block text-sm font-medium text-gray-700 mb-2">
                                    Call-to-Action Type
                                </label>
                                <select
                                    v-model="currentPage.ctaType"
                                    class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                >
                                    <option value="none">None</option>
                                    <option value="add_to_basket">Add to Basket</option>
                                    <option value="view_item">View Item</option>
                                    <option value="view_category">View Category</option>
                                    <option value="external_url">External URL</option>
                                </select>
                            </div>

                            <div v-if="currentPage.ctaType !== 'none'">
                                <label class="block text-sm font-medium text-gray-700 mb-2">
                                    CTA Button Label
                                </label>
                                <input
                                    v-model="currentPage.ctaLabel"
                                    type="text"
                                    placeholder="e.g., Shop Now, Learn More"
                                    class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                            </div>

                            <div v-if="currentPage.ctaType === 'add_to_basket' || currentPage.ctaType === 'view_item'">
                                <label class="block text-sm font-medium text-gray-700 mb-2">
                                    Product ID *
                                </label>
                                <input
                                    v-model="currentPage.ctaData.productId"
                                    type="text"
                                    placeholder="Enter product ID"
                                    class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    :class="{ 'border-red-500': pageErrors.productId }"
                                />
                                <p v-if="pageErrors.productId" class="mt-1 text-sm text-red-600">{{ pageErrors.productId }}</p>
                            </div>

                            <div v-if="currentPage.ctaType === 'view_category'">
                                <label class="block text-sm font-medium text-gray-700 mb-2">
                                    Category ID *
                                </label>
                                <input
                                    v-model="currentPage.ctaData.categoryId"
                                    type="text"
                                    placeholder="Enter category ID"
                                    class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    :class="{ 'border-red-500': pageErrors.categoryId }"
                                />
                                <p v-if="pageErrors.categoryId" class="mt-1 text-sm text-red-600">{{ pageErrors.categoryId }}</p>
                            </div>

                            <div v-if="currentPage.ctaType === 'external_url'">
                                <label class="block text-sm font-medium text-gray-700 mb-2">
                                    URL *
                                </label>
                                <input
                                    v-model="currentPage.ctaData.url"
                                    type="url"
                                    placeholder="https://example.com"
                                    class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    :class="{ 'border-red-500': pageErrors.url }"
                                />
                                <p v-if="pageErrors.url" class="mt-1 text-sm text-red-600">{{ pageErrors.url }}</p>
                            </div>
                        </div>
                    </div>

                    <div class="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                        <button
                            @click="savePage"
                            class="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm"
                        >
                            {{ editingPageIndex !== null ? 'Update Page' : 'Add Page' }}
                        </button>
                        <button
                            @click="closePageModal"
                            class="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:w-auto sm:text-sm"
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import ToggleSidebar from '../components/common/ToggleSidebar';
import UploadcareInline from '../components/widgets/UploadcareInline';
import HlsVideoPlayer from '../components/common/HlsVideoPlayer';

export default {
    name: 'CreateStory',
    metaInfo() {
        return {
            title: this.isEditMode ? 'Edit Story' : 'Create Story'
        };
    },
    components: {
        ToggleSidebar,
        UploadcareInline,
        HlsVideoPlayer
    },
    data() {
        return {
            currentStep: 0,
            steps: [
                { title: 'Story Details', description: 'Add title and cover video' },
                { title: 'Add Pages', description: 'Add pages to your story' },
                { title: 'Assign Venues', description: 'Choose which venues will show this story' },
                { title: 'Complete', description: 'Story created successfully' }
            ],
            story: {
                title: '',
                muxUrl: null,
                muxAsset: null,
                active: true,
                defaultAction: {}
            },
            pages: [],
            createdStoryId: null,
            isLoading: false,
            errors: {},
            showPageModal: false,
            editingPageIndex: null,
            currentPage: {
                muxUrl: null,
                duration: 5,
                ctaType: 'none',
                ctaLabel: '',
                ctaData: {}
            },
            pageErrors: {},
            venues: [],
            selectedVenues: [],
            showAdvancedOptions: false,
            venueAssignments: [],
            isEditMode: false,
            storyId: null,
            isReplacingCoverVideo: false
        };
    },
    async mounted() {
        this.isEditMode = !!this.$route.params.id;
        this.storyId = this.$route.params.id;
        
        await this.fetchVenues();
        
        if (this.isEditMode) {
            await this.fetchStoryData();
        }
    },
    computed: {
        pageTitle() {
            return this.isEditMode ? 'Edit Story' : 'Create Story';
        },
        coverPreviewUrl() {
            if (this.story.muxUrl) {
                return this.story.muxUrl;
            }
            if (this.story.muxAsset && this.story.muxAsset.playbackId) {
                return true;
            }
            return null;
        }
    },
    methods: {
        async fetchStoryData() {
            this.isLoading = true;
            try {
                const { data } = await this.$axios.get(`/stories/${this.storyId}`);
                
                this.story = {
                    title: data.title || '',
                    muxUrl: data.muxUrl || null,
                    muxAsset: data.muxAsset || null,
                    active: data.active !== undefined ? data.active : true,
                    defaultAction: data.defaultAction || {}
                };
                
                this.createdStoryId = data.id;
                
                if (data.pages && data.pages.length > 0) {
                    this.pages = data.pages.map(page => ({
                        muxUrl: page.muxUrl || null,
                        duration: page.duration || 5,
                        ctaType: page.ctaType || 'none',
                        ctaLabel: page.ctaLabel || '',
                        ctaData: page.ctaData || {}
                    }));
                }
                
                if (data.venueAssignments && data.venueAssignments.length > 0) {
                    this.selectedVenues = data.venueAssignments.map(va => va.venueId);
                    this.venueAssignments = data.venueAssignments.map(va => ({
                        venueId: va.venueId,
                        startDate: va.startDate || null,
                        endDate: va.endDate || null,
                        priority: va.priority || 0,
                        active: va.active !== undefined ? va.active : true,
                        overrides: va.overrides || {}
                    }));
                }
            } catch (error) {
                this.$notify({
                    group: 'settings',
                    type: 'error',
                    text: error.response?.data?.message || 'Failed to load story data'
                });
                this.$router.push({ name: 'offers' });
            } finally {
                this.isLoading = false;
            }
        },
        async fetchVenues() {
            try {
                const { data } = await this.$axios.get('/venues');
                this.venues = data;
            } catch {
                this.$notify({
                    group: 'settings',
                    type: 'error',
                    text: 'Failed to load venues'
                });
            }
        },
        getVenueName(venueId) {
            const venue = this.venues.find(v => v.id === venueId);
            return venue ? venue.name : 'Unknown Venue';
        },
        getVenueAssignment(venueId) {
            let assignment = this.venueAssignments.find(a => a.venueId === venueId);
            if (!assignment) {
                assignment = {
                    venueId: venueId,
                    startDate: null,
                    endDate: null,
                    priority: 0,
                    active: true,
                    overrides: {}
                };
                this.venueAssignments.push(assignment);
            }
            return assignment;
        },
        getStepClass(index) {
            if (index < this.currentStep) {
                return 'bg-blue-600 text-white';
            } else if (index === this.currentStep) {
                return 'bg-blue-600 text-white';
            } else {
                return 'bg-gray-300 text-gray-600';
            }
        },
        handleCoverVideoUpload(file) {
            this.story.muxUrl = file.cdnUrl;
            this.errors.muxUrl = null;
        },
        handleCoverVideoRemoved() {
            this.story.muxUrl = null;
        },
        cancelReplaceCoverVideo() {
            this.isReplacingCoverVideo = false;
        },
        handlePageVideoUpload(file) {
            this.currentPage.muxUrl = file.cdnUrl;
            this.pageErrors.muxUrl = null;
        },
        handlePageVideoRemoved() {
            this.currentPage.muxUrl = null;
        },
        formatCtaType(type) {
            const labels = {
                'none': 'None',
                'add_to_basket': 'Add to Basket',
                'view_item': 'View Item',
                'view_category': 'View Category',
                'external_url': 'External URL'
            };
            return labels[type] || type;
        },
        validateStep() {
            this.errors = {};
            let isValid = true;

            if (this.currentStep === 0) {
                if (!this.story.title || this.story.title.trim() === '') {
                    this.errors.title = 'Story title is required';
                    isValid = false;
                }

                if (!this.coverPreviewUrl) {
                    this.errors.muxUrl = 'Cover video is required';
                    isValid = false;
                }
            }

            return isValid;
        },
        validatePage() {
            this.pageErrors = {};
            let isValid = true;

            if (!this.currentPage.muxUrl) {
                this.pageErrors.muxUrl = 'Video is required';
                isValid = false;
            }

            if (!this.currentPage.duration || this.currentPage.duration < 1 || this.currentPage.duration > 60) {
                this.pageErrors.duration = 'Duration must be between 1 and 60 seconds';
                isValid = false;
            }

            if (this.currentPage.ctaType === 'add_to_basket' || this.currentPage.ctaType === 'view_item') {
                if (!this.currentPage.ctaData.productId) {
                    this.pageErrors.productId = 'Product ID is required';
                    isValid = false;
                }
            }

            if (this.currentPage.ctaType === 'view_category') {
                if (!this.currentPage.ctaData.categoryId) {
                    this.pageErrors.categoryId = 'Category ID is required';
                    isValid = false;
                }
            }

            if (this.currentPage.ctaType === 'external_url') {
                if (!this.currentPage.ctaData.url) {
                    this.pageErrors.url = 'URL is required';
                    isValid = false;
                }
            }

            return isValid;
        },
        async nextStep() {
            if (!this.validateStep()) {
                return;
            }

            if (this.currentStep === 0) {
                await this.createStory();
            } else {
                this.currentStep++;
            }
        },
        previousStep() {
            if (this.currentStep > 0) {
                this.currentStep--;
            }
        },
        async createStory() {
            this.isLoading = true;
            try {
                const payload = {
                    title: this.story.title,
                    active: this.story.active,
                    defaultAction: this.story.defaultAction
                };

                if (this.isEditMode) {
                    await this.$axios.put(`/stories/${this.storyId}`, payload);
                    this.$notify({
                        group: 'settings',
                        type: 'success',
                        text: 'Story updated successfully'
                    });
                } else {
                    payload.muxUrl = this.story.muxUrl;
                    const { data } = await this.$axios.post('/stories', payload);
                    this.createdStoryId = data.id;
                    this.$notify({
                        group: 'settings',
                        type: 'success',
                        text: 'Story created successfully'
                    });
                }
                
                this.currentStep++;
            } catch (error) {
                this.$notify({
                    group: 'settings',
                    type: 'error',
                    text: error.response?.data?.message || (this.isEditMode ? 'Failed to update story' : 'Failed to create story')
                });
            } finally {
                this.isLoading = false;
            }
        },
        async finishStory() {
            this.isLoading = true;
            try {
                const storyId = this.isEditMode ? this.storyId : this.createdStoryId;
                
                if (this.isEditMode) {
                    await this.$axios.delete(`/stories/${storyId}/pages`);
                }
                
                if (this.pages.length > 0) {
                    for (let i = 0; i < this.pages.length; i++) {
                        const page = this.pages[i];
                        const payload = {
                            order: i,
                            muxUrl: page.muxUrl,
                            duration: page.duration,
                            ctaType: page.ctaType,
                            ctaLabel: page.ctaLabel || null,
                            ctaData: page.ctaData
                        };

                        await this.$axios.post(`/stories/${storyId}/pages`, payload);
                    }
                }

                if (this.selectedVenues.length > 0) {
                    const venues = this.selectedVenues.map(venueId => {
                        const assignment = this.venueAssignments.find(a => a.venueId === venueId);
                        return {
                            venueId: venueId,
                            startDate: assignment?.startDate || null,
                            endDate: assignment?.endDate || null,
                            priority: assignment?.priority || 0,
                            active: assignment?.active !== undefined ? assignment.active : true,
                            overrides: assignment?.overrides || {}
                        };
                    });

                    if (this.isEditMode) {
                        await this.$axios.put(`/stories/${storyId}/venues`, { venues });
                    } else {
                        await this.$axios.post(`/stories/${storyId}/venues`, { venues });
                    }
                }

                this.currentStep++;
                this.$notify({
                    group: 'settings',
                    type: 'success',
                    text: this.isEditMode ? 'Story updated successfully' : 'Story completed successfully'
                });
            } catch (error) {
                this.$notify({
                    group: 'settings',
                    type: 'error',
                    text: error.response?.data?.message || (this.isEditMode ? 'Failed to update story' : 'Failed to complete story')
                });
            } finally {
                this.isLoading = false;
            }
        },
        openPageModal(index = null) {
            this.editingPageIndex = index;
            if (index !== null) {
                this.currentPage = { ...this.pages[index] };
            } else {
                this.currentPage = {
                    muxUrl: null,
                    duration: 5,
                    ctaType: 'none',
                    ctaLabel: '',
                    ctaData: {}
                };
            }
            this.pageErrors = {};
            this.showPageModal = true;
        },
        closePageModal() {
            this.showPageModal = false;
            this.editingPageIndex = null;
            this.currentPage = {
                muxUrl: null,
                duration: 5,
                ctaType: 'none',
                ctaLabel: '',
                ctaData: {}
            };
            this.pageErrors = {};
        },
        savePage() {
            if (!this.validatePage()) {
                return;
            }

            if (this.editingPageIndex !== null) {
                this.pages[this.editingPageIndex] = { ...this.currentPage };
            } else {
                this.pages.push({ ...this.currentPage });
            }

            this.closePageModal();
        },
        editPage(index) {
            this.openPageModal(index);
        },
        deletePage(index) {
            this.pages.splice(index, 1);
        },
        movePageUp(index) {
            if (index > 0) {
                const temp = this.pages[index];
                this.pages.splice(index, 1);
                this.pages.splice(index - 1, 0, temp);
            }
        },
        movePageDown(index) {
            if (index < this.pages.length - 1) {
                const temp = this.pages[index];
                this.pages.splice(index, 1);
                this.pages.splice(index + 1, 0, temp);
            }
        },
        cancel() {
            this.$router.push({ name: 'offers' });
        },
        goToStories() {
            this.$router.push({ name: 'offers' });
        },
        createAnother() {
            this.currentStep = 0;
            this.story = {
                title: '',
                muxUrl: null,
                active: true,
                defaultAction: {}
            };
            this.pages = [];
            this.createdStoryId = null;
            this.errors = {};
            this.selectedVenues = [];
            this.showAdvancedOptions = false;
            this.venueAssignments = [];
        }
    }
};
</script>
